/**
 * Operation Deduplication Utilities
 * Centralized logic for detecting and skipping operation echoes
 */

import { log } from '@/shared/utils/logger';
import { webSocketSingleton } from '@/shared/api/WebSocketSingleton';

export function isAckOnlySameSessionOperationType(operationType: string | undefined): boolean {
  return (
    operationType === 'update' ||
    operationType === 'batch-update' ||
    operationType === 'progress-update'
  );
}

/**
 * Check if an operation should be skipped (it's our own echo)
 */
export function shouldSkipOperation(
  operationSessionId: string | undefined,
  ourSessionId: string,
  operationId: string,
  operationType: string,
  messageSource?: string
): boolean {
  // Skip if sessionId matches ours exactly
  if (operationSessionId && operationSessionId === ourSessionId) {
    log.debug('WebSocket', '🔴 SKIPPING OWN OPERATION (session match)', {
      operationId,
      type: operationType
    });
    return true;
  }

  // Sessionless broadcasts from REST/API paths are legitimate cross-client updates.
  // Do not infer "our own echo" from a wall-clock heuristic; that drops valid
  // reorder/move broadcasts and leaves one browser stale while another converges.
  if (!operationSessionId && messageSource === 'mcp') {
    log.debug('WebSocket', '✅ PROCESSING MCP OPERATION (sessionless broadcast)', {
      operationId,
      type: operationType,
      source: messageSource
    });
  }

  return false;
}

/**
 * Check if an operation-result should be skipped based on deduplication and canonicity
 */
export function shouldSkipOperationResult(params: {
  operationSessionId: string | undefined;
  messageSessionId: string | undefined;
  ourSessionId: string;
  operationId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- operation has dynamic shape from WebSocket
  operation: any;
  isGroupMove: boolean;
  isCanonical: boolean;
  hasUpdatedNodesFull: boolean;
  hasAuthoritativeNode: boolean;
  messageSource?: string;
}): boolean {
  const {
    operationSessionId,
    messageSessionId,
    ourSessionId,
    operationId,
    operation,
    isGroupMove,
    isCanonical,
    hasUpdatedNodesFull,
    hasAuthoritativeNode,
    messageSource
  } = params;

  // Check if this is our own session
  const isOurSession = operationSessionId === ourSessionId || messageSessionId === ourSessionId;

  if (isOurSession) {
    const alreadyProcessed = webSocketSingleton.isOperationProcessed(operationId, ourSessionId);

    if (alreadyProcessed) {
      const operationType = operation?.type as string | undefined;
      const isMoveOrReorder = operationType === 'move' || operationType === 'reorder';
      const isSingleNodeMove = isMoveOrReorder && !isGroupMove;
      const isDeleteFamily =
        operationType === 'delete' ||
        operationType === 'delete-tree' ||
        operationType === 'batch-delete';
      const isAckOnlySameSessionResult = isAckOnlySameSessionOperationType(operationType);
      const hasAuthoritativeSiblingOrder =
        Array.isArray(operation?.nodeIds) && operation.nodeIds.length > 0;
      const hasAuthoritativeSourceOrder =
        operationType === 'move' &&
        (operation?.sourceParentId !== undefined || Array.isArray(operation?.sourceNodeIds));

      log.debug('WebSocket', '🔍 [DEDUP CHECK]', {
        isGroupMove,
        isCanonical,
        hasUpdatedNodesFull,
        isDeleteFamily,
        isSingleNodeMove,
        hasAuthoritativeSiblingOrder,
        hasAuthoritativeSourceOrder,
        willSkip: isSingleNodeMove && !hasAuthoritativeSiblingOrder && !hasAuthoritativeSourceOrder
      });

      if (isSingleNodeMove && (hasAuthoritativeSiblingOrder || hasAuthoritativeSourceOrder)) {
        log.debug('WebSocket', '✅ [DEDUP] Processing authoritative move/reorder result');
        return false;
      }

      const isGroupMoveWithDateDelta =
        isGroupMove &&
        operation?.daysDelta !== undefined &&
        operation?.daysDelta !== null;

      // Accept canonical or authoritative results for move/reorder/group-move.
      // Group-move can be represented only by daysDelta when updatedNodes is empty,
      // and must still be applied for persistence.
      if ((isGroupMove || isMoveOrReorder) && (isCanonical || hasUpdatedNodesFull || isGroupMoveWithDateDelta)) {
        log.debug('WebSocket', '✅ [DEDUP] Accepting move/group-move result', {
          isCanonical,
          hasUpdatedNodesFull,
          isGroupMoveWithDateDelta,
        });
        return false; // Don't skip - process move/group-move result
      }

      if (isAckOnlySameSessionResult) {
        log.debug('WebSocket', '❌ [DEDUP] Skipping same-session ack-only result', {
          operationType,
          hasAuthoritativeNode,
          isCanonical,
          hasUpdatedNodesFull,
        });
        return true;
      }

      if (hasAuthoritativeNode || isCanonical || hasUpdatedNodesFull) {
        log.debug('WebSocket', '✅ [DEDUP] Accepting authoritative same-session result', {
          operationType,
          hasAuthoritativeNode,
          isCanonical,
          hasUpdatedNodesFull,
        });
        return false;
      }

      if (isDeleteFamily) {
        log.debug('WebSocket', '✅ [DEDUP] Accepting same-session delete-family result', {
          operationType: operation?.type,
        });
        return false;
      }

      // Skip non-canonical, already processed operations
      log.debug('WebSocket', '❌ [DEDUP] Skipping (already processed, not canonical)');
      return true;
    }
  }

  const isSessionlessMcpResult =
    messageSource === 'mcp' && !operationSessionId && !messageSessionId;

  if (isSessionlessMcpResult) {
    const alreadyProcessed = webSocketSingleton.isOperationProcessed(operationId, ourSessionId);
    if (alreadyProcessed) {
      log.debug('WebSocket', '❌ [DEDUP] Skipping duplicate sessionless MCP result', {
        operationId,
        type: operation?.type,
        source: messageSource,
      });
      return true;
    }
  }

  return false;
}

/**
 * Track an operation as processed
 */
export function trackOperation(
  operationId: string,
  sessionId: string,
  processedOperations: Set<string>
): void {
  webSocketSingleton.trackOperation(operationId, sessionId);
  processedOperations.add(operationId);

  // Clean up old operation IDs to prevent memory leak (keep last 100)
  if (processedOperations.size > 100) {
    const ops = Array.from(processedOperations);
    const newSet = new Set(ops.slice(-100));
    processedOperations.clear();
    newSet.forEach(op => processedOperations.add(op));
  }
}

/**
 * Initialize last operation sent timestamp
 */
export function initializeOperationTracking(): void {
  if (!(window as unknown as Record<string, unknown>).__lastOperationSent) {
    (window as unknown as Record<string, unknown>).__lastOperationSent = 0;
  }
}
